'use client';

import type { AuthSnapshot } from '@lib/data/auth-snapshot';
import { getLayoutSnapshot } from '@lib/data/layout-snapshot';
import UnreadSummaryNavRefresh from '@modules/account/components/unread-summary-loader/nav-refresh';
import BrazeIdentity from '@modules/analytics/components/braze-identity';
import HealthConsentReconciler from '@modules/analytics/components/health-consent-reconciler';
import { useAuth } from '@modules/common/components/auth-provider';
import { AUTH_CHANGED_EVENT } from '@modules/common/components/auth-provider/constants';
import { useCart } from '@modules/common/components/cart-provider';
import * as Sentry from '@sentry/nextjs';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

const LOGGED_OUT: AuthSnapshot = { isLoggedIn: false };

/**
 * Fetches auth + cart state client-side (via `getLayoutSnapshot`) and
 * rehydrates `AuthProvider`/`CartProvider`. Neither is fetched in
 * `(main)/layout.tsx` directly — reading those cookies there would force the
 * whole route group to render dynamically on every request.
 *
 * Refetches on:
 * - every navigation (`pathname` change) — mirrors `UnreadSummaryNavRefresh`'s
 *   own pathname-driven refetch, since the layout persists across
 *   in-`(main)` navigations and would otherwise never see fresh data.
 * - `AUTH_CHANGED_EVENT` — login/logout redirect back to `/account` by
 *   *default* (the same URL the login form itself lives at), so `pathname`
 *   never changes for the common case. `AuthChangedSignal`, mounted by
 *   `/account/*`'s layout (which always re-checks auth fresh), dispatches
 *   this event instead as a URL-independent signal.
 *
 * Also mounts the customer-dependent bits that used to render inline in the
 * layout once logged in is confirmed: the unread-messages refresher, the
 * health-consent reconciler, and (gated on the `braze_enabled` flag, resolved
 * server-side in the snapshot) Braze identify.
 */
export default function LayoutDataLoader() {
  const { rehydrate: rehydrateAuth, setShowNewsletterSignup } = useAuth();
  const { rehydrate: rehydrateCart, getMutationVersion } = useCart();
  const [auth, setAuth] = useState<AuthSnapshot>(LOGGED_OUT);
  const pathname = usePathname();

  const refresh = useCallback(() => {
    let cancelled = false;
    const mutationVersionAtStart = getMutationVersion();

    getLayoutSnapshot()
      .then(
        ({ auth: authSnapshot, cart: cartSnapshot, showNewsletterSignup }) => {
          if (cancelled) {
            return;
          }
          rehydrateAuth(authSnapshot);
          setShowNewsletterSignup(showNewsletterSignup);
          // `CartProvider`'s own `CART_CHANGED_EVENT` listener may have
          // written a fresher cart (an add/update/remove) while this fetch
          // was in flight. Applying this snapshot's now-stale cart would
          // revert that mutation, so skip it when the version moved on.
          if (getMutationVersion() === mutationVersionAtStart) {
            rehydrateCart(cartSnapshot);
          }
          setAuth(authSnapshot);
        },
      )
      .catch((error) => {
        if (cancelled) {
          return;
        }
        // Leave Auth/CartProvider at their safe logged-out/empty defaults —
        // just make sure the failure is visible instead of silent.
        Sentry.captureException(error, {
          extra: { context: 'LayoutDataLoader:getLayoutSnapshot' },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    rehydrateAuth,
    rehydrateCart,
    setShowNewsletterSignup,
    getMutationVersion,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is an intentional trigger — the effect re-runs on navigation but doesn't reference pathname in its body
  useEffect(() => refresh(), [pathname, refresh]);

  useEffect(() => {
    let cancelPrevious = () => {};

    const handleAuthChanged = () => {
      cancelPrevious();
      cancelPrevious = refresh();
    };

    window.addEventListener(AUTH_CHANGED_EVENT, handleAuthChanged);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, handleAuthChanged);
      cancelPrevious();
    };
  }, [refresh]);

  if (!auth.isLoggedIn) {
    return null;
  }

  return (
    <>
      <UnreadSummaryNavRefresh />
      {/* Reconcile pre-login marketing consent to the profile. NOT gated
          on braze_enabled — it feeds email gating, not Braze. */}
      <HealthConsentReconciler
        customerId={auth.customerId}
        profileHealthConsent={auth.profileHealthConsent}
      />
      {auth.brazeEnabled && (
        <BrazeIdentity
          customerId={auth.customerId}
          customerEmail={auth.customerEmail}
          customerFirstName={auth.customerFirstName}
          customerLastName={auth.customerLastName}
        />
      )}
    </>
  );
}
